import { useState, useRef, useEffect, useMemo } from "react";
import { Search } from "lucide-react";

// A text-styled trigger that opens a small popover to pick from a list.
// Click on the value text → popover opens. Search box auto-appears for
// lists with more than 8 options. All clicks stopPropagation so the cell
// click never bubbles up to a clickable row.
//
// Props:
//   value           — current value (matched against option.value)
//   options         — [{ value, label }, ...] or strings (auto-wrapped)
//   onChange(value) — called when an option is picked
//   placeholder     — shown when nothing is selected
//   tone            — "blue" (default) | "muted"
//   searchable      — force on/off (defaults: auto when options > 8)
export default function InlinePicker({
  value, options = [], onChange, placeholder = "—", tone = "blue", searchable,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const normalized = useMemo(
    () => options.map((o) => (typeof o === "string" ? { value: o, label: o } : o)),
    [options]
  );
  const showSearch = searchable ?? normalized.length > 8;

  useEffect(() => {
    if (!open) { setQuery(""); return; }
    if (showSearch) {
      // tiny delay so the input mounts before we focus
      requestAnimationFrame(() => searchRef.current?.focus());
    }
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, showSearch]);

  const current = normalized.find((o) => o.value === value);
  const trigger = current ? current.label : placeholder;

  const filtered = useMemo(() => {
    if (!query.trim()) return normalized;
    const q = query.toLowerCase();
    return normalized.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [normalized, query]);

  const TONES = {
    blue: "text-brand-blue hover:text-brand-blue-dark",
    muted: "text-brand-slate hover:text-brand-ink",
    green: "text-emerald-600 hover:text-emerald-700",
    red: "text-red-600 hover:text-red-700",
    amber: "text-amber-600 hover:text-amber-700",
  };
  const toneCls = TONES[tone] || TONES.blue;

  const handlePick = (val) => { onChange(val); setOpen(false); };

  const onSearchKey = (e) => {
    if (e.key === "Enter" && filtered.length > 0) {
      e.preventDefault();
      handlePick(filtered[0].value);
    }
  };

  return (
    <span ref={wrapRef} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className={`${toneCls} text-xs underline decoration-dotted underline-offset-2 cursor-pointer truncate max-w-full text-left`}
      >
        {trigger}
      </button>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute z-50 mt-1 left-0 min-w-[220px] bg-white dark:bg-[var(--bg-surface)] rounded-lg shadow-card-hover border border-gray-100 dark:border-[var(--border-soft)] overflow-hidden"
        >
          {showSearch && (
            <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-100 dark:border-[var(--border-soft)]">
              <Search size={12} className="text-brand-slate shrink-0" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKey}
                placeholder="Search…"
                className="w-full text-xs bg-transparent focus:outline-none placeholder:text-brand-slate"
              />
            </div>
          )}
          <div className="max-h-[240px] overflow-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-brand-slate italic">No matches</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value || opt.label}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handlePick(opt.value); }}
                  className={`w-full text-left px-3 py-1.5 text-xs whitespace-nowrap hover:bg-gray-50 dark:hover:bg-[var(--bg-soft)] ${
                    value === opt.value
                      ? "text-brand-blue font-medium bg-brand-blue-soft/50"
                      : "text-brand-ink dark:text-[var(--text-primary)]"
                  }`}
                >
                  {opt.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </span>
  );
}
